import {
  CompanyDto,
  FileDto,
  ForemanDailyTask,
  ForemanEmployeeDispatch,
  ForemanEquipmentDispatch,
  ForemanJobCrew,
  ForemanMaterialExpendable,
  ForemanPhase,
} from '@ups/xplat/api/dto';
import { Guid } from '@ups/xplat/core';
import moment from 'moment';
import { Observable } from 'rxjs';
import type { ForemanJobService } from './foreman-job.service';

/** Result of {@link createForemanCrew}; `reactivatedFromInactive` when a deactivated crew was turned back on by name. */
export type CreateForemanCrewResult = ForemanJobCrew & {
  reactivatedFromInactive?: boolean;
};

export function getTasks(service: ForemanJobService): ForemanDailyTask[] {
  return service
    .selectedJob()
    .tasks.filter(
      (m) =>
        m.bActive === true &&
        !m.isNextShiftTask &&
        moment(m.dateWorked).format('MM/DD/YYYY') ===
          moment(service.selectedDate()).format('MM/DD/YYYY') &&
        (!service.selectedShift() || m.shift === service.selectedShift()) &&
        m.foremanJobCrewID === service.selectedForemanCrewID()
    )
    .sort((a, b) => a.taskOrder - b.taskOrder)
    .map((t) => ({
      ...t,
      isFavorite: !!service.favoriteTaskMap.get(t.foremanTaskID),
    }));
}

export function getNextShiftTasks(
  service: ForemanJobService
): ForemanDailyTask[] {
  const currentShift = service.selectedShift();
  const currentDate = service.selectedDate();
  const targetDate =
    currentShift === 2
      ? moment(currentDate).add(1, 'day').toDate()
      : currentDate;

  return service
    .selectedJob()
    .tasks.filter(
      (m) =>
        m.bActive === true &&
        m.isNextShiftTask === true &&
        moment(m.dateWorked).format('MM/DD/YYYY') ===
          moment(targetDate).format('MM/DD/YYYY') &&
        m.foremanJobCrewID === service.selectedForemanCrewID()
    )
    .sort((a, b) => a.taskOrder - b.taskOrder)
    .map((t) => ({
      ...t,
      isFavorite: !!service.favoriteTaskMap.get(t.foremanTaskID),
    }));
}

export function updateTasks(
  service: ForemanJobService,
  tasks: ForemanDailyTask[]
) {
  const currentJob = service.selectedJob();
  if (!currentJob) return;

  for (const task of tasks) {
    const jobTask = currentJob.tasks.find(
      (t) => t.foremanDailyTaskID === task.foremanDailyTaskID
    );

    if (!jobTask) {
      currentJob.tasks = [...currentJob.tasks, task];
    } else {
      jobTask.bActive = task.bActive;
      jobTask.isFavorite = task.isFavorite;
      jobTask.taskDescription = task.taskDescription;
      jobTask.taskOrder = task.taskOrder;
      jobTask.isNextShiftTask = task.isNextShiftTask;
    }
  }

  updateFavoriteTasks(service, tasks);
  service.selectedJob.set({ ...currentJob });
}

export function updateFavoriteTasks(
  service: ForemanJobService,
  tasks: ForemanDailyTask[]
) {
  for (const task of tasks) {
    const favoriteTask = service
      .favoriteTasks()
      .find((t) => t.foremanTaskID === task.foremanTaskID);

    if (task.isFavorite && !favoriteTask) {
      service.setFavoriteTasks([
        ...service.favoriteTasks(),
        {
          foremanTaskID: task.foremanTaskID,
          taskDescription: task.taskDescription,
          isFavorite: true,
          bActive: true,
        },
      ]);
    }

    if (favoriteTask && !task.isFavorite) {
      service.setFavoriteTasks(
        service
          .favoriteTasks()
          .filter((t) => t.foremanTaskID !== task.foremanTaskID)
      );
    }
  }
}

export function getMaterialExpendables(
  service: ForemanJobService
): ForemanMaterialExpendable[] {
  return service
    .selectedJob()
    .materialExpendables.filter(
      (m) =>
        m.bActive === true &&
        moment(m.dateWorked).format('MM/DD/YYYY') ===
          moment(service.selectedDate()).format('MM/DD/YYYY') &&
        (!service.selectedShift() || m.shift === service.selectedShift()) &&
        m.foremanJobCrewID === service.selectedForemanCrewID()
    );
}

export function updateMaterials(
  service: ForemanJobService,
  materials: ForemanMaterialExpendable[]
) {
  const currentJob = service.selectedJob();
  if (!currentJob) return;

  for (const material of materials) {
    const jobMaterialIndex = currentJob.materialExpendables.findIndex(
      (m) =>
        m.foremanMaterialExpendableID === material.foremanMaterialExpendableID
    );

    if (jobMaterialIndex === -1) {
      currentJob.materialExpendables.push(material);
    } else {
      currentJob.materialExpendables[jobMaterialIndex].bActive =
        material.bActive;
      currentJob.materialExpendables[jobMaterialIndex].description =
        material.description;
      currentJob.materialExpendables[jobMaterialIndex].quantity =
        material.quantity;
      currentJob.materialExpendables[jobMaterialIndex].totalPrice =
        material.totalPrice;
    }
  }

  service.selectedJob.set({ ...currentJob });
}

export function createWorkOrder(
  service: ForemanJobService,
  workOrder: string,
  description: string
) {
  const newWorkOrder: ForemanPhase = {
    foremanPhaseID: Guid.newGuid(),
    phaseName: workOrder,
    description,
    phaseID: null,
  };

  service.selectedJob.set({
    ...service.selectedJob(),
    workOrders: [...(service.selectedJob()?.workOrders ?? []), newWorkOrder],
  });

  return newWorkOrder;
}

export function createForemanCrew(
  service: ForemanJobService,
  id: string,
  foremanCrewName: string,
  shift: number = null
): CreateForemanCrewResult | null {
  id = id || Guid.newGuid();
  foremanCrewName = (foremanCrewName || '').trim();

  if (!foremanCrewName.length) return null;

  const existingForemanCrew = service
    .selectedJob()
    ?.foremanCrews?.find((c) => c.name === foremanCrewName);

  if (existingForemanCrew) {
    if (existingForemanCrew.bActive === false) {
      const job = service.selectedJob();
      const crews = (job.foremanCrews ?? []).map((c) =>
        c.foremanJobCrewID === existingForemanCrew.foremanJobCrewID
          ? { ...c, bActive: true }
          : c
      );
      service.selectedJob.set({ ...job, foremanCrews: crews });
      return {
        ...existingForemanCrew,
        bActive: true,
        reactivatedFromInactive: true,
      };
    }
    return existingForemanCrew;
  }

  const newForemanCrew: ForemanJobCrew = {
    foremanJobCrewID: id,
    name: foremanCrewName,
    jobCrewID: null,
    shift,
    bActive: true,
  };

  service.selectedJob.set({
    ...service.selectedJob(),
    foremanCrews: [
      ...(service.selectedJob()?.foremanCrews ?? []),
      newForemanCrew,
    ],
  });

  return newForemanCrew;
}

export function updateEmployee(
  service: ForemanJobService,
  employee: ForemanEmployeeDispatch
) {
  const currentJob = service.selectedJob();
  if (!currentJob) return;
  const employeeId = service.getEmployeeId(employee);
  if (!employeeId) return;

  const employeeIndex = currentJob.employees.findIndex(
    (e) => service.getEmployeeId(e) === employeeId
  );

  if (employeeIndex > -1) {
    currentJob.employees[employeeIndex] = { ...employee };
  }

  service.selectedJob.set({ ...currentJob });
}

export function updateEquipment(
  service: ForemanJobService,
  equipment: ForemanEquipmentDispatch
) {
  const currentJob = service.selectedJob();
  if (!currentJob) return;
  const equipmentId = service.getEquipmentId(equipment);
  if (!equipmentId) return;

  const equipmentIndex = currentJob.equipment.findIndex(
    (e) => service.getEquipmentId(e) === equipmentId
  );

  if (equipmentIndex > -1) {
    currentJob.equipment[equipmentIndex] = { ...equipment };
  }

  service.selectedJob.set({ ...currentJob });
}

export function isChanged(service: ForemanJobService) {
  if (!service.selectedJob() || !service.selectedDate()) {
    return false;
  }

  const isChangedLaborsForDate = service
    .selectedJob()
    .employees.some((employee) =>
      (employee.labors || []).some(
        (labor) =>
          new Date(labor.dateWorked).toDateString() ===
            service.selectedDate().toDateString() &&
          labor.isChanged &&
          (!service.selectedShift() ||
            labor.shift === service.selectedShift()) &&
          labor.foremanJobCrewID === service.selectedForemanCrewID()
      )
    );

  const isChangedUsagesForDate = service
    .selectedJob()
    .equipment.some((equipment) =>
      (equipment.usages || []).some(
        (usage) =>
          new Date(usage.dateUsed).toDateString() ===
            service.selectedDate().toDateString() &&
          usage.isChanged &&
          (!service.selectedShift() ||
            usage.shift === service.selectedShift()) &&
          usage.foremanJobCrewID === service.selectedForemanCrewID()
      )
    );

  return isChangedLaborsForDate || isChangedUsagesForDate;
}

export function fetchCompanies(
  service: ForemanJobService
): Observable<CompanyDto[]> {
  return service.httpPascalCase.get('/api/companies') as Observable<
    CompanyDto[]
  >;
}

export function setPdfFile(service: ForemanJobService, file: FileDto) {
  service.pdfFile.set(file);
}

export function downloadPdfByFileId(
  service: ForemanJobService,
  fileId: string
): Observable<Blob> {
  return service.http.get(`/api/file/downloadfile/${fileId}`, {
    responseType: 'blob',
  }) as Observable<Blob>;
}
